import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import OrderDTO from "@/dto/order/OrderDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import OrderStatus from "@/enum/OrderStatus";
import OrderTag from "@/enum/OrderTag";
import ShopType from "@/enum/ShopType";
import { EwaybillGenerateResult } from "@/services/api/ewaybillGenerate";
import { matchDoubleAddresses } from "@/services/doubleAddressChecker";
import { AutoPrintPostPayInterval, AutoPrintTaskType } from "@/services/api/autoPrint";
import PackingListTemplateDTO from "@/dto/template/PackingListTemplateDTO";
import { SelectedProductItem } from "@/components/BatchAddProductModal/store";
import { EwaybillCopiesPreset } from "@/components/EwaybillCopiesSelector";
interface CommonFormValues {
    shopIds: number[];
    postPayInterval: AutoPrintPostPayInterval;
    provinceCodes: string[];
    orderRemarkType?: OrderRemarkType;
    products?: SelectedProductItem[];
    maxPrintOrderCount?: number;
    /**
     * 订单来源
     * 数组第1个值 表示是否为非带货达人订单。false 为非带货达人订单
     * 数组第2个值 表示 带货达人OriginalId
     */
    fromOrigin?: [boolean, string];
    specifiedSplitQuantity?: number;
    maxWeightPerPackage?: number;
    defaultUnitWeight?: number;
}

export type AutoPrintSettingFormValues = AutoPrintPackingSettingFormValues | AutoPrintWaybillSettingFormValues;

export type AutoPrintPackingSettingFormValues = {
    packListTemplate: PackingListTemplateDTO;
    printerName: string;
} & CommonFormValues & {
        type: AutoPrintTaskType.PACKING_LIST;
    };

export type AutoPrintWaybillSettingFormValues = {
    [key in MultiShopSelectPlatformType]?: {
        template: WaybillTemplateDTO;
        printerName: "string";
        useChildWaybillNoMode: boolean;
    };
} & {
    copies: EwaybillCopiesPreset;
    autoShipAfterPrint: boolean;
    shippingAddress: ShippingAddressDTO;
} & CommonFormValues & {
        type: AutoPrintTaskType.WAYBILL;
    };

/**
 * 因为自动打印订单，有可能出现重复订单打印
 * 所以传统的1对1映射mapping无法使用
 * 故此这里直接在对应的原订单上进行操作数据
 */
export interface AutoPrintOrderDTO extends Omit<OrderDTO, "childOrders"> {
    /**
     * 跳过结果
     */
    _skipResult?: {
        message: string;
    };

    _ewaybillGenerateResult?: EwaybillGenerateResult;

    /**
     * 打印结果
     */
    _printResult?: {
        status: boolean;
        message?: string;
    };

    /**
     * 发货结果(子订单发货结果从子订单对象中获取)
     */
    _shipResult?: {
        status: boolean;
        message?: string;
    };

    /**
     * 使用的电子面单模板
     */
    _waybillTemplate: WaybillTemplateDTO;

    _packListTemplate: PackingListTemplateDTO;

    /**
     * 操作时间
     */
    _opTime: string;

    childOrders?: AutoPrintOrderDTO[];
}

/**
 * 内部使用的参数 回传给后端的
 *
 * @see {@link AutoPrintSettingFormValues}
 */
export type InternalOpenAutoPrintSettingParams =
    | InternalAutoPrintWaybillSettingParams
    | InternalAutoPrintPackingSettingParams;

interface InternalAutoPrintPackingSettingParams extends Omit<AutoPrintPackingSettingFormValues, "packListTemplate"> {
    packListTemplate: Pick<PackingListTemplateDTO, "id" | "name">;
    /** 从products中转化，用于日志 */
    productInfos?: InternalAutoPrintWaybillSettingProductInfo[];
}

export interface InternalAutoPrintWaybillSettingProductInfo {
    productOriginalId: string;
    skuOriginalId: string;
}
type InternalAutoPrintWaybillSettingParams = Omit<
    AutoPrintWaybillSettingFormValues,
    "shippingAddress" | MultiShopSelectPlatformType
> &
    {
        [key in MultiShopSelectPlatformType]?: {
            template: Pick<WaybillTemplateDTO, "id" | "name" | "type" | "logisticsCompany">;
            printerName: "string";
            useChildWaybillNoMode?: boolean;
        };
    } & {
        shippingAddress: Pick<
            ShippingAddressDTO,
            | "id"
            | "province"
            | "city"
            | "district"
            | "street"
            | "shipperName"
            | "name"
            | "telephone"
            | "zipCode"
            | "mobile"
        >;
    } & {
        /** 从products中转化，用于日志 */
        productInfos?: InternalAutoPrintWaybillSettingProductInfo[];
    };

/**
 * 支持自动打印订单的店铺类型
 */
export const autoPrintSupportShopTypes = [
    ShopType.TAOBAO,
    ShopType.PINDUODUO,
    ShopType.KTT,
    ShopType.DOUDIAN,
    ShopType.KWAI,
    ShopType.CHANNEL_SHOP,
    ShopType.XIAOHONGSHU,
];

interface OrderErrorValidation {
    /**
     * 返回 true 代表抛出错误
     */
    validator: (order: OrderDTO) => Promise<boolean> | boolean;
    message: string;
}

export function isContainProvince(order: OrderDTO, provinceCodes?: string[]) {
    if (!provinceCodes?.length) {
        return true;
    }

    if (!order.provinceCode) {
        return false;
    }

    const contain = provinceCodes.indexOf(order.provinceCode.toString()) !== -1;

    return contain;
}

/**
 * 选择了商品，则自动打印只打印仅包含选中的商品的订单，即订单仅包含选择的商品和赠品（赠品可以为非选择的商品），不能包含其他未选择的商品。
 * 例如：一个订单 O 有三个 OrderItem，分别是 A、B、C，打印筛选商品中选择了 A 和 B 这两个商品，但是 C 没选中，这个订单就会被跳过。
 */
export function isContainProduct(order: OrderDTO, selectedProductItems: SelectedProductItem[]) {
    // 如果没有选择商品，则全部订单都符合条件。
    if (!selectedProductItems?.length) {
        return true;
    }

    // 构建所有被选中的商品（含 sku 和 product 的 originalId）以及 shopId 的集合。
    const selectedList = selectedProductItems.map(({ productOriginalId, skuOriginalId, shopId }) => ({
        productOriginalId,
        skuOriginalId,
        shopId,
    }));

    // 检查订单中所有非赠品商品项。
    for (const orderItem of order.orderItems) {
        // 跳过赠品。
        if (orderItem.isGift) continue;
        // 检查该商品项是否在选择列表中。
        const matched = selectedList.some((sel) => {
            if (sel.shopId !== order.shopId) return false;
            if (sel.skuOriginalId) {
                return (
                    orderItem.skuOriginalId === sel.skuOriginalId &&
                    orderItem.productOriginalId === sel.productOriginalId
                );
            } else {
                return orderItem.productOriginalId === sel.productOriginalId;
            }
        });
        // 如果有一个非赠品商品项不在选择列表中，则不符合条件。
        if (!matched) {
            // 『订单仅包含选择的商品和赠品，不能包含其他未选择的商品。』
            return false;
        }
    }
    // 所有非赠品商品项都在选择列表中，符合条件。
    return true;
}

/** @todo 这里要补充不能打印发货的订单限制场景 */
// 非待发货订单，退款中订单，部分发货订单，已打印订单，挂起订单，代发订单
// 风控订单，代收货款订单，部分数量发货订单，异常订单，锁定订单 黑名单订单
// 地址异常，双地址订单，不可达订单
export const defaultOmitPrintOrderErrorValidations: OrderErrorValidation[] = [
    {
        message: "非待发货订单",
        validator(order) {
            return order.status !== OrderStatus.PENDING_SHIPMENT;
        },
    },
    {
        message: "退款中订单",
        validator(order) {
            return order.isRefunding;
        },
    },
    {
        message: "部分发货订单",
        validator(order) {
            return order.isPartShipped;
        },
    },
    {
        message: "已打印订单",
        validator(order) {
            return order.isWaybillPrinted;
        },
    },
    {
        message: "挂起订单",
        validator(order) {
            return order.isOnHold;
        },
    },
    {
        message: "代发订单",
        validator(order) {
            return !!order.retailerDropShippingInfo || !!order.supplierDropShippingInfo;
        },
    },
    {
        message: "代收货款订单",
        validator(order) {
            return !!order.codAmounts;
        },
    },
    {
        message: "部分数量发货订单",
        validator(order) {
            return !!order.tags?.includes(OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED);
        },
    },
    {
        message: "异常订单",
        validator(order) {
            return !!order.tags?.includes(OrderTag.ABNORMAL_ORDER);
        },
    },
    {
        message: "锁定订单",
        validator(order) {
            return !!order.tags?.includes(OrderTag.LOCKED);
        },
    },
    {
        message: "黑名单订单",
        validator(order) {
            return !!order.tags?.includes(OrderTag.BUYER_BLACKLIST);
        },
    },
    {
        message: "双地址订单",
        async validator(order) {
            if (order.tags?.includes(OrderTag.DOUBLE_ADDRESS)) {
                return true;
            }

            const resultMap = await matchDoubleAddresses([order]);

            const result = resultMap?.[`${order?.shopId}_${order?.id}`];

            if (result?.isMatch) {
                return true;
            }

            return false;
        },
    },
];

/**
 * 筛选备注类型
 */
export enum OrderRemarkType {
    /**
     * 无留言且无备注
     */
    NO_BUYER_REMARK_AND_NO_SELLER_REMARK = "NO_BUYER_REMARK_AND_NO_SELLER_REMARK",
    /**
     * 无留言
     */
    NO_BUYER_REMARK = "NO_BUYER_REMARK",
    /**
     * 无备注
     */
    NO_SELLER_REMARK = "NO_SELLER_REMARK",

    /**
     * 有卖家留言
     */
    HAS_SELLER_REMARK = "HAS_SELLER_REMARK",
}

/**
 * 筛选备注类型
 */
export const OrderRemarkTypeNameMapping = {
    [OrderRemarkType.NO_BUYER_REMARK_AND_NO_SELLER_REMARK]: "无留言且无备注",
    [OrderRemarkType.NO_BUYER_REMARK]: "无留言",
    [OrderRemarkType.NO_SELLER_REMARK]: "无备注",
    [OrderRemarkType.HAS_SELLER_REMARK]: "有备注",
};

/**
 * 筛选出通过符合条件的，不符合条件的跳过订单，默认是不跳过的
 */
export function orderRemarkTypeValidator(order: OrderDTO, orderRemarkType: OrderRemarkType) {
    switch (orderRemarkType) {
        case OrderRemarkType.NO_BUYER_REMARK_AND_NO_SELLER_REMARK: {
            return !order.buyerRemarks && !order.sellerRemarks;
        }
        case OrderRemarkType.NO_BUYER_REMARK: {
            return !order.buyerRemarks;
        }
        case OrderRemarkType.NO_SELLER_REMARK: {
            return !order.sellerRemarks;
        }
        case OrderRemarkType.HAS_SELLER_REMARK: {
            return !!order.sellerRemarks;
        }

        default:
            return true;
    }
}
